'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  Radar,
  Loader2,
  ArrowRight,
  Globe,
  CheckCircle2,
  XCircle,
  Sparkles,
  Info,
} from 'lucide-react'
import { gradeFromScore } from '@/lib/radar/scoring'

// Free-tier tools only. All take just { url }, no brand required.
// aeo-auditor was removed here when it went paid-first (2026-06-26): its free
// route now returns 402 for non-paid callers, so keeping its column would show a
// broken/locked cell in an otherwise free showcase.
const COMPARE_TOOLS: Array<{
  key: string
  name: string
  endpoint: string
  color: string
}> = [
  {
    key: 'crawl',
    name: 'Crawl',
    endpoint: '/api/tools/crawl-check',
    color: '#008dcc',
  },
  {
    key: 'robots',
    name: 'Robots.txt',
    endpoint: '/api/tools/robots-analyzer',
    color: '#8b5cf6',
  },
  {
    key: 'llms',
    name: 'llms.txt',
    endpoint: '/api/tools/llms-validator',
    color: '#22c55e',
  },
  {
    key: 'readiness',
    name: 'Readiness',
    endpoint: '/api/tools/ai-readiness',
    color: '#FDC304',
  },
  {
    key: 'schemaAudit',
    name: 'Schema',
    endpoint: '/api/tools/schema-audit',
    color: '#14b8a6',
  },
]

type ToolResult = {
  status: 'idle' | 'running' | 'done' | 'error'
  score: number | null
  grade: string | null
}

type DomainState = Record<string, ToolResult>

function emptyState(): DomainState {
  return Object.fromEntries(
    COMPARE_TOOLS.map(t => [
      t.key,
      { status: 'idle' as const, score: null, grade: null },
    ])
  )
}

function normalizeUrl(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function brandFromDomain(domain: string): string {
  const base = domain.replace(/^www\./, '').split('.')[0]
  return base.charAt(0).toUpperCase() + base.slice(1)
}

export default function CompareClient() {
  const router = useRouter()
  const [urlA, setUrlA] = useState('')
  const [urlB, setUrlB] = useState('')
  const [running, setRunning] = useState(false)
  const [stateA, setStateA] = useState<DomainState>(emptyState())
  const [stateB, setStateB] = useState<DomainState>(emptyState())
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [complete, setComplete] = useState(false)

  function unifiedScore(state: DomainState): number {
    const scores = Object.values(state)
      .filter(s => s.status === 'done' && typeof s.score === 'number')
      .map(s => s.score as number)
    if (scores.length === 0) return 0
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
  }

  async function runTool(
    tool: (typeof COMPARE_TOOLS)[number],
    url: string,
    setter: React.Dispatch<React.SetStateAction<DomainState>>
  ) {
    setter(s => ({ ...s, [tool.key]: { ...s[tool.key], status: 'running' } }))
    try {
      const res = await fetch(tool.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      if (!res.ok) {
        setter(s => ({
          ...s,
          [tool.key]: { status: 'error', score: null, grade: null },
        }))
        return
      }
      const data = await res.json()
      const score =
        typeof data.overallScore === 'number'
          ? Math.round(data.overallScore)
          : typeof data.score === 'number'
            ? Math.round(data.score)
            : 0
      const grade = data.grade || gradeFromScore(score)
      setter(s => ({
        ...s,
        [tool.key]: { status: 'done', score, grade },
      }))
    } catch {
      setter(s => ({
        ...s,
        [tool.key]: { status: 'error', score: null, grade: null },
      }))
    }
  }

  async function handleCompare() {
    setError(null)
    const finalA = normalizeUrl(urlA)
    const finalB = normalizeUrl(urlB)
    if (!finalA || !finalB) {
      setError('Enter both URLs.')
      return
    }
    try {
      new URL(finalA)
      new URL(finalB)
    } catch {
      setError('One of the URLs is invalid.')
      return
    }
    if (domainFromUrl(finalA) === domainFromUrl(finalB)) {
      setError('Enter two different domains.')
      return
    }

    setRunning(true)
    setComplete(false)
    setStateA(emptyState())
    setStateB(emptyState())

    // Run all 12 tool calls in parallel (6 per domain)
    const all = [
      ...COMPARE_TOOLS.map(t => runTool(t, finalA, setStateA)),
      ...COMPARE_TOOLS.map(t => runTool(t, finalB, setStateB)),
    ]
    await Promise.allSettled(all)
    setRunning(false)
    setComplete(true)
  }

  async function handleShare() {
    setSaving(true)
    const finalA = normalizeUrl(urlA)
    const finalB = normalizeUrl(urlB)
    const domainA = domainFromUrl(finalA)
    const domainB = domainFromUrl(finalB)
    const scoreA = unifiedScore(stateA)
    const scoreB = unifiedScore(stateB)

    const toolScoresA: Record<string, { score: number; grade: string }> = {}
    const toolScoresB: Record<string, { score: number; grade: string }> = {}
    for (const t of COMPARE_TOOLS) {
      const a = stateA[t.key]
      const b = stateB[t.key]
      if (a.status === 'done' && a.score !== null && a.grade) {
        toolScoresA[t.key] = { score: a.score, grade: a.grade }
      }
      if (b.status === 'done' && b.score !== null && b.grade) {
        toolScoresB[t.key] = { score: b.score, grade: b.grade }
      }
    }

    try {
      const res = await fetch('/api/tools/compare/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domainA: domainA,
          brandA: brandFromDomain(domainA),
          scoreA,
          gradeA: gradeFromScore(scoreA),
          toolScoresA,
          domainB: domainB,
          brandB: brandFromDomain(domainB),
          scoreB,
          gradeB: gradeFromScore(scoreB),
          toolScoresB,
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.shareId) {
        setError(data.message || 'Could not save comparison.')
        setSaving(false)
        return
      }
      router.push(`/tools/compare/${data.shareId}`)
    } catch {
      setError('Network error. Try again.')
      setSaving(false)
    }
  }

  const scoreA = unifiedScore(stateA)
  const scoreB = unifiedScore(stateB)
  const gradeA = gradeFromScore(scoreA)
  const gradeB = gradeFromScore(scoreB)
  const doneCountA = Object.values(stateA).filter(
    s => s.status === 'done'
  ).length
  const doneCountB = Object.values(stateB).filter(
    s => s.status === 'done'
  ).length

  return (
    <div className='container mx-auto px-4 py-16 max-w-5xl'>
      {/* Hero */}
      <div className='text-center mb-12'>
        <div
          className='inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6'
          style={{ background: '#8b5cf610', border: '1px solid #8b5cf630' }}
        >
          <Sparkles size={12} style={{ color: '#8b5cf6' }} />
          <span
            className='text-xs font-semibold uppercase tracking-widest'
            style={{ color: '#8b5cf6' }}
          >
            Free Comparison Tool
          </span>
        </div>
        <h1 className='text-4xl md:text-5xl font-black mb-4 text-foreground'>
          AI Visibility Comparison
        </h1>
        <p className='text-base md:text-lg max-w-2xl mx-auto text-muted-foreground'>
          See how two domains stack up on AI visibility. 6 technical readiness
          tools run on each site. Results in 60 seconds. Shareable URL.
        </p>
      </div>

      {/* Methodology note */}
      <div
        className='rounded-xl p-4 mb-6 flex gap-3 items-start'
        style={{
          background: '#FDC30408',
          border: '1px solid #FDC30430',
        }}
      >
        <Info
          size={16}
          className='flex-shrink-0 mt-0.5'
          style={{ color: '#FDC304' }}
        />
        <div className='text-xs text-foreground/80 leading-relaxed'>
          <span className='font-semibold text-foreground'>
            What Radar actually measures.
          </span>{' '}
          This tool scores content-surface AI visibility: how easily AI models
          can crawl, parse, and cite a single URL. Results are most meaningful
          for marketing sites, blogs, and content platforms. Tools, search
          engines, SaaS dashboards, and JS-heavy apps often score low because
          their homepages aren&rsquo;t content by design. For multi-subdomain
          brands (google.com, microsoft.com), the homepage score
          under-represents the brand&rsquo;s real AI surface.
        </div>
      </div>

      {/* Input */}
      <div className='rounded-2xl border border-border bg-card p-6 mb-8'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
          <div>
            <label className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2'>
              <Globe size={12} />
              Domain A
            </label>
            <input
              type='url'
              value={urlA}
              onChange={e => setUrlA(e.target.value)}
              placeholder='https://stripe.com'
              disabled={running}
              className='w-full h-12 px-4 rounded-lg border text-sm outline-none focus:ring-2'
              style={{
                background: 'var(--background)',
                borderColor: 'var(--border)',
                color: 'var(--foreground)',
              }}
            />
          </div>
          <div>
            <label className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2'>
              <Globe size={12} />
              Domain B
            </label>
            <input
              type='url'
              value={urlB}
              onChange={e => setUrlB(e.target.value)}
              placeholder='https://paddle.com'
              disabled={running}
              className='w-full h-12 px-4 rounded-lg border text-sm outline-none focus:ring-2'
              style={{
                background: 'var(--background)',
                borderColor: 'var(--border)',
                color: 'var(--foreground)',
              }}
            />
          </div>
        </div>

        {error && (
          <p className='text-xs mb-3' style={{ color: '#F90B8A' }}>
            {error}
          </p>
        )}

        <button
          type='button'
          onClick={handleCompare}
          disabled={running || !urlA.trim() || !urlB.trim()}
          className='w-full h-12 rounded-lg text-sm font-bold text-white transition-all hover:brightness-110 disabled:opacity-40 flex items-center justify-center gap-2'
          style={{
            background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
          }}
        >
          {running ? (
            <>
              <Loader2 size={14} className='animate-spin' />
              Running {doneCountA + doneCountB}/12 tools…
            </>
          ) : (
            <>
              <Radar size={14} />
              Compare
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {(running || complete) && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
          <DomainCard
            label='Domain A'
            displayDomain={urlA ? domainFromUrl(normalizeUrl(urlA)) : ''}
            state={stateA}
            score={scoreA}
            grade={gradeA}
            running={running}
          />
          <DomainCard
            label='Domain B'
            displayDomain={urlB ? domainFromUrl(normalizeUrl(urlB)) : ''}
            state={stateB}
            score={scoreB}
            grade={gradeB}
            running={running}
          />
        </div>
      )}

      {/* Winner + Share CTA */}
      {complete && !running && (
        <div className='rounded-2xl border border-border bg-card p-6 mb-8 text-center'>
          {scoreA !== scoreB ? (
            <>
              <p className='text-xs uppercase tracking-widest text-muted-foreground mb-2'>
                Winner
              </p>
              <p className='text-xl font-bold text-foreground mb-4'>
                {scoreA > scoreB
                  ? domainFromUrl(normalizeUrl(urlA))
                  : domainFromUrl(normalizeUrl(urlB))}{' '}
                leads by {Math.abs(scoreA - scoreB)} points
              </p>
            </>
          ) : (
            <p className='text-xl font-bold text-foreground mb-4'>
              Tie at {scoreA}/100
            </p>
          )}
          <button
            type='button'
            onClick={handleShare}
            disabled={saving}
            className='inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50'
            style={{
              background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
            }}
          >
            {saving ? (
              <>
                <Loader2 size={14} className='animate-spin' />
                Saving…
              </>
            ) : (
              <>
                Get shareable URL
                <ArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      )}

      {/* Upsell */}
      <div
        className='rounded-2xl p-6 text-center'
        style={{
          background:
            'linear-gradient(135deg, #008dcc08, #8b5cf608, #F90B8A08)',
          border: '1px solid #8b5cf630',
        }}
      >
        <p className='text-sm font-semibold text-foreground mb-2'>
          Want 6 more tools (Citations, Reddit, Hallucination, Prompt SOV,
          Source Influence, Answer Engine)?
        </p>
        <p className='text-xs text-muted-foreground mb-4'>
          Run the full 13-tool audit from $5. Credits never expire.
        </p>
        <a
          href='/platform#pricing'
          className='inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white transition-all hover:brightness-110'
          style={{
            background: 'linear-gradient(135deg, #8b5cf6, #F90B8A)',
          }}
        >
          Unlock all 13 tools
          <ArrowRight size={14} />
        </a>
      </div>
    </div>
  )
}

function DomainCard({
  label,
  displayDomain,
  state,
  score,
  grade,
  running,
}: {
  label: string
  // Already-extracted hostname for display. Never a raw URL so no href/redirect
  // risk exists here (closes the Snyk CWE-601 flag on the callsite).
  displayDomain: string
  state: DomainState
  score: number
  grade: string
  running: boolean
}) {
  const gradeColors: Record<string, string> = {
    A: '#22c55e',
    B: '#008dcc',
    C: '#eab308',
    D: '#f97316',
    F: '#F90B8A',
  }
  const color = gradeColors[grade] || '#F90B8A'
  const label_or_domain = displayDomain || label

  return (
    <div className='rounded-2xl border border-border bg-card p-6'>
      <p className='text-xs uppercase tracking-widest text-muted-foreground mb-1'>
        {label}
      </p>
      <p
        className='text-sm font-semibold text-foreground mb-4 truncate'
        title={label_or_domain}
      >
        {label_or_domain}
      </p>
      <div className='flex items-baseline gap-3 mb-4'>
        <span className='text-5xl font-black' style={{ color }}>
          {score}
        </span>
        <span className='text-sm text-muted-foreground'>/ 100</span>
        <span
          className='ml-auto px-2.5 py-1 rounded-md text-xs font-bold'
          style={{ background: `${color}20`, color }}
        >
          {grade}
        </span>
      </div>
      <div className='space-y-2'>
        {COMPARE_TOOLS.map(t => {
          const s = state[t.key]
          const icon =
            s.status === 'running' ? (
              <Loader2
                size={12}
                className='animate-spin text-muted-foreground'
              />
            ) : s.status === 'done' ? (
              <CheckCircle2 size={12} style={{ color: t.color }} />
            ) : s.status === 'error' ? (
              <XCircle size={12} style={{ color: '#F90B8A' }} />
            ) : (
              <div
                className='w-3 h-3 rounded-full'
                style={{ background: 'var(--border)' }}
              />
            )
          return (
            <div key={t.key} className='flex items-center gap-2 text-xs'>
              {icon}
              <span className='text-foreground flex-1'>{t.name}</span>
              <span className='font-mono text-muted-foreground'>
                {s.status === 'done' ? `${s.score}` : '—'}
              </span>
            </div>
          )
        })}
      </div>
      {!running && Object.values(state).every(s => s.status === 'idle') && (
        <p className='text-xs text-muted-foreground mt-4 italic'>
          Enter a URL to run
        </p>
      )}
    </div>
  )
}
